import {
  defaultReceiveQtyMode,
  type ReceiveQtyMode,
} from "@/lib/manufacturing/receiveQtyConversion";

export type ReceiveSearchMatch = {
  id: string;
  name: string;
  code: string;
};

export type ReceiveSearchAddDecision =
  | { action: "blank" }
  | { action: "add"; materialId: string }
  | { action: "none" }
  | { action: "choose" };

/**
 * What "+ Add Line" should do after a receive-panel material search.
 * An empty search still appends a blank row. A single match, or one
 * exact code/name hit among looser matches, is the material to receive.
 * Several matches must be picked explicitly so Add Line does not append
 * another empty row.
 */
export function resolveReceiveSearchAdd(
  query: string,
  matches: readonly ReceiveSearchMatch[],
): ReceiveSearchAddDecision {
  const q = query.trim().toLowerCase();
  if (!q) return { action: "blank" };
  const exact = matches.filter((m) => {
    const code = m.code.trim().toLowerCase();
    const name = m.name.trim().toLowerCase();
    return code === q || name === q;
  });
  if (exact.length === 1) return { action: "add", materialId: exact[0].id };
  if (matches.length === 1) return { action: "add", materialId: matches[0].id };
  if (matches.length === 0) return { action: "none" };
  return { action: "choose" };
}

type ReceiveMaterialLine = {
  materialId: string;
  qtyMode: ReceiveQtyMode;
  aliasId: string;
};

/**
 * Put `materialId` on the first line that does not have one yet.
 * Keeps qty, cost, and notes already typed on that row. Appends only
 * when every existing line already has a material.
 */
export function assignReceiveMaterial<T extends ReceiveMaterialLine>(
  lines: readonly T[],
  materialId: string,
  material:
    | {
        unit: string;
        shipping_unit: string;
        pieces_per_shipping_unit: number;
      }
    | undefined,
  makeBlank: () => T,
): T[] {
  const nextFields: ReceiveMaterialLine = {
    materialId,
    qtyMode: material
      ? defaultReceiveQtyMode({
          unit: material.unit,
          shipping_unit: material.shipping_unit,
          pieces_per_shipping_unit: Number(material.pieces_per_shipping_unit) || 1,
        })
      : "canonical",
    aliasId: "",
  };
  const blankIdx = lines.findIndex((l) => !l.materialId);
  if (blankIdx >= 0) {
    return lines.map((l, i) => (i === blankIdx ? { ...l, ...nextFields } : l));
  }
  return [...lines, { ...makeBlank(), ...nextFields }];
}
